import React, { memo, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { toSnakeCase } from 'utils/transformText'
import { MACHINE_QUARTERS, MACHINE_STATES, MACHINE_TYPES } from 'constants/machines'
import { gameSettingsSelector } from 'store/user/selectors'
import { selectMachine } from 'store/investments'
import { sellMachine as sell } from 'store/production'
import MachineInstalling from 'components/Machines/MachineInstalling'
import ActionButton from 'components/Shared/ActionButton'
import MoneyUnit from 'components/MoneyUnit'
import AssignedProduct from 'components/Machines/AssignedProduct'
import MachineQuarter from 'components/Machines/MachineQuarter'
import MachineWorkers from 'components/Machines/MachineWorkers'
import MachineStand from 'components/Machines/MachineStand'
import YearsInUse from 'components/Machines/YearsInUse'
import PlantActionButton from 'components/Plant/PlantActionButton'
import MachineStartButton from 'components/Machines/MachineStartButton'

function Machine(props) {
  const {
    className,
    currentSlot,
    empty,
    handleConfirmModal,
    id,
    installationTime,
    machineType,
    maintenanceCosts,
    product,
    productChangeover,
    productType,
    salvageValue,
    sellMachine,
    status,
    superWorkers,
    totalInstallationTime,
    withQuarters,
    workers,
    yearsInUse
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const machineVariant = useMemo(() => toSnakeCase(machineType), [machineType])
  const variantClass = useMemo(() => MACHINE_TYPES[machineVariant], [machineVariant])
  const emptyClass = empty ? 'machine--empty' : ''

  const quarters = useMemo(
    () => [...Array(MACHINE_QUARTERS[machineVariant] + 1).keys()].slice(1),
    [machineVariant]
  )
  const isProducing = status === MACHINE_STATES.producing
  const producingClass = isProducing ? 'producing' : ''

  const settings = useSelector(gameSettingsSelector)?.machines_configuration[machineVariant]
  const { product_changeover_cost: productChangeoverCost } = settings || {
    product_changeover_cost: 0
  }

  const handleSelectMachine = useCallback(() => {
    dispatch(selectMachine(id))
  }, [id])

  const sellMachineAction = useCallback(() => {
    dispatch(sell(id))
    handleConfirmModal(null)
  }, [handleConfirmModal, id])

  const handleSellMachine = useCallback(() => {
    handleConfirmModal({
      action: sellMachineAction,
      title: t('investment.sell_machine.confirm.title'),
      description: t('investment.sell_machine.confirm.description')
    })
  }, [handleConfirmModal, id])

  if (status === MACHINE_STATES.installing) {
    return (
      <MachineInstalling from={installationTime} to={totalInstallationTime} type={machineVariant} />
    )
  }

  return (
    <div className={`machine ${className} ${emptyClass}`}>
      <div className="machine__box">
        <div className={`machine__band machine__band--${variantClass} ${producingClass}`}>
          <AssignedProduct
            className="machine__assigned-product"
            cost={productChangeoverCost}
            empty={empty}
            machineType={variantClass}
            productType={productType}
          />
          {withQuarters ? (
            <div className={`machine__quarter machine__quarter--${variantClass}`}>
              {quarters.map((quarter) => (
                <MachineQuarter
                  machineType={variantClass}
                  quarter={quarter}
                  product={currentSlot === quarter && product}
                  isProducing={isProducing}
                  empty={empty}
                />
              ))}
            </div>
          ) : null}
          {!empty && (
            <>
              <MachineWorkers
                className="machine__workers"
                superWorkers={superWorkers}
                workers={workers}
                isProducing={isProducing}
              />
              <div className="machine__status">
                <MachineStartButton machineId={id} status={status} productType={productType} />
              </div>
            </>
          )}
          <MachineStand variant={variantClass} />
        </div>
      </div>
      {!empty && (
        <div className="flex-row justify-end">
          <YearsInUse years={yearsInUse} />
          <MoneyUnit coins={maintenanceCosts} className="ml-1" />
        </div>
      )}
      {productChangeover && status === MACHINE_STATES.installed && (
        <ActionButton className="machine__select-button" handleOnClick={handleSelectMachine}>
          <div>{t('machine.select')}</div>
        </ActionButton>
      )}
      {sellMachine && status === MACHINE_STATES.installed && (
        <PlantActionButton
          callAction={handleSellMachine}
          className="machine__sell-button"
          cost={salvageValue}
          text={t('machine.sell_machine')}
          variant="negative"
        />
      )}
    </div>
  )
}

Machine.propTypes = {
  className: PropTypes.string,
  currentSlot: PropTypes.number.isRequired,
  handleConfirmModal: PropTypes.func,
  id: PropTypes.number.isRequired,
  installationTime: PropTypes.number.isRequired,
  empty: PropTypes.bool,
  machineType: PropTypes.string.isRequired,
  maintenanceCosts: PropTypes.number.isRequired,
  product: PropTypes.shape({
    cost: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  productChangeover: PropTypes.bool,
  productType: PropTypes.string.isRequired,
  salvageValue: PropTypes.number.isRequired,
  sellMachine: PropTypes.bool,
  status: PropTypes.string.isRequired,
  superWorkers: PropTypes.number.isRequired,
  totalInstallationTime: PropTypes.number.isRequired,
  withQuarters: PropTypes.bool,
  workers: PropTypes.number.isRequired,
  yearsInUse: PropTypes.number.isRequired
}

Machine.defaultProps = {
  className: '',
  empty: false,
  handleConfirmModal: null,
  product: null,
  productChangeover: false,
  sellMachine: false,
  withQuarters: true
}

export default memo(Machine)
